import emojiData from '../assets/EmojiWithCategories.json';

class EmojiUtils {
    constructor() {
        this.emojiMap = new Map();
        this.flattenedEmojis = [];
        this.recentEmojis = [];
        this.defaultEmojiIds = [];
        this.lastUpdated = null;
        this.fallbackEmoji = null;
        this.init();
    }

    init() {
        // Flatten the emoji data for easy lookup
        // Support both old flat structure and new categories-based structure

        const processEmoji = (emoji, parentId = null, group = null) => {
            const normalized = { ...emoji, parentId };
            if (group && !normalized.group) normalized.group = group;

            // Add to map
            if (normalized.emojiId) {
                this.emojiMap.set(normalized.emojiId, normalized);
            }

            // If it's a top-level emoji (not a variant), add to flattened list for the picker
            if (!parentId) {
                this.flattenedEmojis.push(normalized);
            }

            // Process variants if any (support multiple possible variant property names)
            const variants = emoji.skinToneVariants || emoji.variants || [];
            if (variants && variants.length) {
                variants.forEach(variant => processEmoji(variant, emoji.emojiId, group));
            }
        };

        // Reset collections
        this.emojiMap.clear();
        this.flattenedEmojis = [];

        if (!emojiData) return;

        // metadata
        if (emojiData.last_updated) this.lastUpdated = emojiData.last_updated;
        if (emojiData.defaultEmojiIds && Array.isArray(emojiData.defaultEmojiIds)) {
            this.defaultEmojiIds = [...emojiData.defaultEmojiIds];
        }

        // fallback can be a string id or an object
        if (emojiData.fallbackEmojiId) {
            if (typeof emojiData.fallbackEmojiId === 'string') {
                this.fallbackEmoji = emojiData.fallbackEmojiId;
            } else if (typeof emojiData.fallbackEmojiId === 'object' && emojiData.fallbackEmojiId.emojiId) {
                this.fallbackEmoji = emojiData.fallbackEmojiId.emojiId;
                const fb = emojiData.fallbackEmojiId;
                // add fallback object into collections if provided
                if (fb && fb.emojiId) {
                    this.emojiMap.set(fb.emojiId, fb);
                    this.flattenedEmojis.push(fb);
                }
            }
        }

        // categories-based structure
        if (emojiData.categories && Array.isArray(emojiData.categories)) {
            emojiData.categories.forEach(category => {
                const groupName = category.name || category.group || null;
                const catEmojis = category.emojis || [];
                catEmojis.forEach(e => processEmoji(e, null, groupName));
            });
        } else if (emojiData.emojis && Array.isArray(emojiData.emojis)) {
            // backward compat: flat list under `emojis`
            emojiData.emojis.forEach(e => processEmoji(e));
        }
    }

    /**
     * Get all top-level emojis
     * @returns {Array} Array of emoji objects
     */
    getAllEmojis() {
        return this.flattenedEmojis;
    }

    /**
     * Get a specific emoji by ID (including variants)
     * @param {string} emojiId
     * @returns {Object|undefined} Emoji object
     */
    getEmojiById(emojiId) {
        return this.emojiMap.get(emojiId);
    }

    /**
     * Search emojis by keyword
     * @param {string} query
     * @returns {Array} Filtered list of top-level emojis
     */
    searchEmojis(query) {
        if (!query) return this.flattenedEmojis;

        const lowerQuery = query.toLowerCase().trim();

        return this.flattenedEmojis.filter(emoji => {
            if (!emoji.searchKeys) return false;

            // Check if any search key contains the query
            return emoji.searchKeys.some(key => key.toLowerCase().includes(lowerQuery));
        });
    }

    /**
     * Get grouped emojis for the picker
     * @returns {Object} Map of groupName -> Array of emojis
     */
    getGroupedEmojis() {
        const groups = {};
        this.flattenedEmojis.forEach(emoji => {
            const group = emoji.group || 'Other';
            if (!groups[group]) groups[group] = [];
            groups[group].push(emoji);
        });
        return groups;
    }

    /**
     * Get default emojis for quick reactions
     * @returns {Array} Array of emoji objects
     */
    getDefaultEmojis() {
        if (!this.defaultEmojiIds || !this.defaultEmojiIds.length) {
            return [];
        }
        return this.defaultEmojiIds.map(id => this.getEmojiById(id)).filter(Boolean);
    }
}

export const emojiUtils = new EmojiUtils();
